import { useEffect, useState } from 'react';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { ProductVariant } from 'types/Product';
import { FspService } from 'services/FSP/fast-selling-product-service';
import { isNotInStock } from 'utilities/util';
import { StockCode } from 'types/ProductResponseType';

const extractOptionFromSkus = (data: ProductVariant[]): string[] =>
  data.filter((item) => item.stock && !isNotInStock(item.stock.code)).map((item) => item.option);

interface UseFSPStockProps {
  csrfToken: string;
  isFSPEnabled: boolean;
  fspMockEnabled?: boolean;
  xMockResponseValue?: string;
}

export const useFSPStock = ({ csrfToken, isFSPEnabled, fspMockEnabled, xMockResponseValue }: UseFSPStockProps) => {
  const { skus, setSkus } = useSelectedSkuContext();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFSPEnabled) {
      setLoading(false);
      return;
    }

    const getFSP = async () => {
      try {
        const FSP = new FspService(fspMockEnabled);
        const variantIds = extractOptionFromSkus(skus);

        const response = fspMockEnabled
          ? await FSP.getFSP(csrfToken, variantIds, xMockResponseValue)
          : await FSP.getFSP(csrfToken, variantIds);

        if (response && Array.isArray(response.items)) {
          let hasOutdatedStock = false;

          const updatedSkus = skus.map((sku) => {
            const stockData = response.items.find((item) => item.variantId === sku.option);

            if (stockData?.stockLevel === 0) {
              hasOutdatedStock = true;
              return {
                ...sku,
                stock: {
                  code: StockCode.UNAVAILABLE,
                  primary: 'Unavailable',
                  secondary: 'Sorry, this item is currently unavailable',
                },
              };
            }

            return sku;
          });

          if (hasOutdatedStock) {
            setSkus(updatedSkus);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching FSP data:', error);
        setLoading(false);
      }
    };

    getFSP();
  }, []);

  return { loading };
};
